/**
 * Recommendation-acceptance efficacy signal. Compares the units the
 * dispatcher actually dispatched against the units we recommended (the RECOMMENDED-bucketed set they
 * saw pre-checked). Pure + counts-only — no PII flows out (unit ids are @LogSafe, but we emit only
 * counts and a boolean, never the ids themselves).
 */
export type RecommendationOutcome = {
    /** Units we recommended and showed pre-checked (the RECOMMENDED bucket). */
    recommendedCount: number;
    /** How many of those recommended units the dispatcher actually dispatched (kept). */
    acceptedCount: number;
    /** True iff every recommended unit was dispatched (none dropped). */
    fullyAccepted: boolean;
};

export function computeRecommendationOutcome(
    recommendedUnitIds: ReadonlySet<number>,
    dispatchedUnitIds: readonly number[]
): RecommendationOutcome {
    const recommendedCount = recommendedUnitIds.size;
    // Measured against the RECOMMENDED set ONLY. acceptedCount counts recommended units that were
    // dispatched, so pre-committed HELD/already-dispatched units in dispatchedUnitIds (which are never
    // in recommendedUnitIds) cannot distort it. Deliberately NOT comparing total dispatched counts —
    // an event with held units would otherwise always read as "not accepted" even when every
    // recommendation was kept (the bug a total-count exactMatch had).
    const acceptedCount = dispatchedUnitIds.filter((id) => recommendedUnitIds.has(id)).length;
    const fullyAccepted = recommendedCount > 0 && acceptedCount === recommendedCount;
    return { recommendedCount, acceptedCount, fullyAccepted };
}
